import { type Compatibility, versionCompatibility } from "@ronin/core";
import { toast } from "@ronin/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { authStatusQuery } from "../api.js";
import { isNative } from "../platform/native.js";
import { APP_VERSION } from "../version.js";

/** Whether the server runs another version than this page (dev servers report "dev"). */
export const isOutdated = (serverVersion: string | undefined) =>
  !!serverVersion && serverVersion !== "dev" && serverVersion !== APP_VERSION;

/**
 * spec07 FR4: after a deploy, open tabs still run the old web app. The session is checked
 * every 5 minutes and on focus; a different server version asks for a reload.
 */
export function useVersionCheck(): Compatibility {
  const { data } = useQuery({
    ...authStatusQuery,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  // The app cannot reload into a new version; it blocks on a major or minor difference
  // instead (spec08 FR9) and ignores patch differences.
  const outdated = !isNative && isOutdated(data?.version);
  useEffect(() => {
    if (!outdated) return;
    toast("A new version of Ronin is available.", {
      id: "new-version",
      duration: Number.POSITIVE_INFINITY,
      action: { label: "Reload", onClick: () => window.location.reload() },
    });
  }, [outdated]);
  return isNative && data?.version ? versionCompatibility(APP_VERSION, data.version) : "ok";
}
